import {
  DATE_FORMAT,
  type DateFormatter,
  type IconName,
} from '@dev-spendesk/grapes';
import { type ReactNode } from 'react';

import { PanelIconItemsSection } from 'common/components/Panel';
import {
  type TGlobalFunctionTyped,
  useTranslation,
} from 'common/hooks/useTranslation';

import type { Payable } from '../../containers/PayablePanelContainer/hooks/usePayableQuery/payable';
import {
  getIsPayableExported,
  getIsPayableFullyPaid,
  getIsPayableNotBookkept,
  getIsPayablePrepared,
} from '../../models/payable';

export const PayablePanelStatus = ({
  payable,
  panelActions,
}: {
  payable: Payable;
  panelActions?: ReactNode;
}) => {
  const { t, localeFormat } = useTranslation('global');

  const isPayableFullyPaid = getIsPayableFullyPaid(payable);
  const isPayablePrepared = getIsPayablePrepared(payable);
  const isPayableExported = getIsPayableExported(payable);
  const isPayableNotBookkept = getIsPayableNotBookkept(payable);

  return (
    <PanelIconItemsSection
      inline
      items={
        panelActions
          ? []
          : [
              payable.subtype === 'creditNote'
                ? getCreditStatus(payable, t)
                : {
                    label: getReadablePayablePaidStatus(payable, t),
                    value:
                      getReadablePayablePaidDate(payable, t, localeFormat) ??
                      '',
                    variant: isPayableFullyPaid ? 'success' : 'info',
                    icon: isPayableFullyPaid
                      ? 'circle-check'
                      : 'calendar-clock',
                  },
              {
                label: getReadablePayableExportStatus(
                  isPayablePrepared,
                  isPayableExported,
                  isPayableNotBookkept,
                  t,
                ),
                value: getFirstExportedAtDate(payable, t, localeFormat) ?? '',
                variant: isPayableExported ? 'success' : 'info',
                icon: getReadablePayableExportIcon(
                  isPayableExported,
                  isPayableNotBookkept,
                ),
              },
            ].filter(
              (
                value,
              ): value is {
                label: string;
                value: string;
                icon: IconName;
                variant: 'info' | 'success';
              } => Boolean(value),
            )
      }
      panelActions={panelActions}
    />
  );
};

/**
 * Utils
 */

const getFirstExportedAtDate = (
  payable: Payable,
  translator: TGlobalFunctionTyped,
  localeFormat: DateFormatter,
): string | undefined => {
  if (payable.firstExportedAt) {
    return translator('payables.panel.status.exportedOn', {
      date: localeFormat(payable.firstExportedAt, DATE_FORMAT.MEDIUM),
    });
  }
};

const getReadablePayablePaidStatus = (
  payable: Payable,
  translator: TGlobalFunctionTyped,
): string => {
  const settlementKind = payable.allocations[0]?.settlement?.kind;
  const isPayableFullyPaid = getIsPayableFullyPaid(payable);

  if (isPayableFullyPaid) {
    switch (settlementKind) {
      case 'refund':
        return translator('payables.panel.status.refunded');
      case 'reimbursement':
        return translator('payables.panel.status.reimbursed');
      default:
        return translator('payables.panel.status.paid');
    }
  }

  switch (payable.type) {
    case 'cardPurchase':
    case 'invoicePurchase':
      return translator('payables.panel.status.toPay');
    case 'claimedBill':
    case 'mileageAllowance':
    case 'perDiemAllowance':
      return translator('payables.panel.status.toReimburse');
    default:
      return '';
  }
};

const getReadablePayablePaidDate = (
  payable: Payable,
  translator: TGlobalFunctionTyped,
  localeFormat: DateFormatter,
): string | undefined => {
  if (payable.allocations.some((allocation) => allocation.reversal)) {
    return;
  }

  const sortedAllocations = payable.allocations.sort(
    (allocation1, allocation2) =>
      (allocation2.settlement?.transaction.clearingDate.getTime() || 0) -
      (allocation1.settlement?.transaction.clearingDate.getTime() || 0),
  );
  const latestAllocation = sortedAllocations[0];

  if (latestAllocation && latestAllocation.settlement) {
    const formattedDate = localeFormat(
      latestAllocation.settlement.transaction.clearingDate,
      DATE_FORMAT.MEDIUM,
    );
    return translator('payables.panel.status.settledOn', {
      date: formattedDate,
    });
  }
};

const getReadablePayableExportStatus = (
  isPayablePrepared: boolean,
  isExported: boolean,
  isPayableNotBookkept: boolean,
  translator: TGlobalFunctionTyped,
): string => {
  if (isPayableNotBookkept) {
    return translator('payables.panel.status.notBookkept');
  }
  if (isExported) {
    return translator('payables.panel.status.exported');
  }
  if (isPayablePrepared) {
    return translator('payables.panel.status.toExport');
  }
  return translator('payables.panel.status.toPrepare');
};

const getReadablePayableExportIcon = (
  isPayableExported: boolean,
  isPayableNotBookkept: boolean,
): IconName => {
  if (isPayableExported) {
    return 'circle-check';
  }
  if (isPayableNotBookkept) {
    return 'eye-strikeout';
  }
  return 'clock-outline';
};

type Status = {
  label: string;
  value: string;
  variant: string;
  icon: IconName;
};

const getCreditStatus = (
  payable: Payable,
  t: TGlobalFunctionTyped,
): Status | undefined => {
  if (payable.subtype === 'creditNote') {
    if (
      payable.creditNoteDetails?.validationAction === 'recordForFutureInvoices'
    ) {
      return {
        label: t('payables.panel.creditStatus.available'),
        variant: 'info',
        icon: 'shopping-bag',
        value: '',
      };
    }

    if (payable.creditNoteDetails?.state === 'reimbursed') {
      return {
        label: t('payables.panel.creditStatus.refunded'),
        variant: 'success',
        icon: 'building-bank',
        value: '',
      };
    }

    if (
      payable.creditNoteDetails?.validationAction ===
        'deductFromInvoiceRequests' &&
      payable.creditNoteDetails?.state === 'allocated'
    ) {
      return {
        label: t('payables.panel.creditStatus.deducted'),
        variant: 'success',
        icon: 'circle-check',
        value: '',
      };
    }

    if (
      payable.creditNoteDetails?.validationAction ===
        'deductFromInvoiceRequests' &&
      payable.creditNoteDetails?.state === 'partiallyAllocated'
    ) {
      return {
        label: t('payables.panel.creditStatus.partiallyDeducted'),
        variant: 'success',
        icon: 'circle-check',
        value: '',
      };
    }
  }
};
